import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { HLSFragmentSelectorType, HLSPlaybackMode } from '@aws-sdk/client-kinesis-video-archived-media';
import { Milliseconds } from '@iotv/iotv-v3-types';
import { MenuItem, Select, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useKVStreams } from '../../common/useKVStreams';
import { memo, useContext, useEffect } from 'react';
import { useModularTimePeriodSelector } from '../../../../hooks/useModularTimePeriodSelector';
import { KinesisVideoStreamContext } from '../../Context/KinesisVideoStreamContext';
import { AWS_KVS_Player, HLS_Player } from '../../common/player';
import { useFragmentDetector } from '../../common/useFragmentDetector';
import { VideoStreamRequestControls } from './Controls';
import { usePlayerState } from '../../common/usePlayerState';
import { useSearchParams } from "react-router-dom";
const suppressChecks = process.env.NODE_ENV === 'development' && false;
const checkIsLiveIntervalInMs = suppressChecks ? Milliseconds.MINUTE * 15 : Milliseconds.SECOND * 15;
const VideoMonitorTabComponent = ({}) => {
    console.log(`VideoMonitorTab rerender`);
    const { state } = useContext(KinesisVideoStreamContext);
    return state.kvClient ? _jsx(VideoMonitorTabInternal, {}) : null;
};
export const VideoMonitorTab = memo(VideoMonitorTabComponent, () => true);
const VideoMonitorTabInternalComponent = ({}) => {
    console.log(`VideoMonitorTabInternal rerender`);
    const { state, dispatch } = useContext(KinesisVideoStreamContext);
    const tempBaseDate = dayjs().subtract(10, 'seconds');
    const maxToTimestamp = Math.min(tempBaseDate.valueOf());
    const { fromDateValue, toDateValue, components: { TimePeriodSelector, confirmButton }, } = useModularTimePeriodSelector({ rangeLength: 30, rangeUnit: 'minutes', initialToTime: dayjs(maxToTimestamp), requireConfirmation: true });
    const fromTimestamp = fromDateValue.unix() * Milliseconds.SECOND;
    const toTimestamp = toDateValue.unix() * Milliseconds.SECOND;
    const [searchParams] = useSearchParams();
    const params = Object.fromEntries(searchParams.entries());
    const { tabName, selectedHLSPlaybackMode: selectedHLSPlaybackModeParam, timestamp: timestampParam } = params;
    useEffect(() => {
        if (selectedHLSPlaybackModeParam) {
            // handle the selected HLS playback mode from the URL params
            dispatch({ selectedHLSPlaybackMode: selectedHLSPlaybackModeParam });
        }
        if (timestampParam) {
            // handle the timestamp from the URL params
            const toTimestamp = Number(timestampParam);
            const fromTimestamp = Number(timestampParam) - Milliseconds.MINUTE * 5; // example: 5 minutes before the toTimestamp
            dispatch({ toTimestamp, fromTimestamp });
        }
    }, [selectedHLSPlaybackModeParam, timestampParam]);
    console.log(`TEMP PARAMS`, params);
    useEffect(() => {
        if (state.fromTimestamp !== fromTimestamp || state.toTimestamp !== toTimestamp) {
            dispatch({ fromTimestamp, toTimestamp });
        }
    }, [dispatch, fromTimestamp, toTimestamp]);
    useEffect(() => {
        return () => {
            console.log(`Cleaning up VideoMonitorTabInternal time period selector`);
            dispatch({ fromTimestamp: null, toTimestamp: null });
        };
    }, [dispatch]);
    const { LiveVideoIndicator } = useFragmentDetector({ checkIsLiveIntervalInMs });
    const { VideoHub, VideoStream, kvClient, selectedStreamInfo, selectedHLSPlaybackMode, selectedHLSFragmentSelectorType } = state;
    const { streamName } = useKVStreams({});
    console.log(`VideoMonitorTabInternal streamName`, streamName, `selectedHLSPlaybackMode`, selectedHLSPlaybackMode, `selectedHLSFragmentSelectorType`, selectedHLSFragmentSelectorType);
    const modes = [HLSPlaybackMode.LIVE, HLSPlaybackMode.LIVE_REPLAY, HLSPlaybackMode.ON_DEMAND];
    const modeSelector = (_jsx(Select, { sx: { width: '10rem' },
        size: 'small',
        value: selectedHLSPlaybackMode,
        select: true,
        children: modes.map((mode) => (_jsx(MenuItem, { value: mode, children: mode }, mode))),
        onChange: (e) => e.target.value && dispatch({ selectedHLSPlaybackMode: e.target.value }) }));
    const fragmentTypes = [HLSFragmentSelectorType.PRODUCER_TIMESTAMP, HLSFragmentSelectorType.SERVER_TIMESTAMP];
    const fragmentTypeSelector = (_jsx(Select, { sx: { width: '10rem' },
        size: 'small',
        value: selectedHLSFragmentSelectorType,
        select: true,
        children: fragmentTypes.map((type) => (_jsx(MenuItem, { value: type, children: type }, type))),
        onChange: (e) => e.target.value && dispatch({ selectedHLSFragmentSelectorType: e.target.value }) }));
    const { playerState, playerEventFns } = usePlayerState({});
    const getRecordedVideoIsRequesable = () => VideoStream?.captureDurationInMinutes
        ? Math.max(fromTimestamp, toTimestamp) > Date.now() - VideoStream.captureDurationInMinutes * Milliseconds.MINUTE
        : false;
    const recordedVideoIsRequesable = getRecordedVideoIsRequesable();
    return (_jsxs(Stack, { direction: 'column', spacing: 2, alignItems: 'flex-start', width: '100%', children: [_jsxs(Stack, { direction: 'row', children: [modeSelector, fragmentTypeSelector] }), _jsxs(Stack, { direction: 'row', spacing: 2, alignItems: 'center', children: [_jsx(Stack, { children: LiveVideoIndicator }), _jsx(Stack, { children: _jsx(VideoStreamRequestControls, { fromTimestamp: fromTimestamp, toTimestamp: toTimestamp, recordedVideoIsRequesable }) })] }), _jsxs(Stack, { children: [selectedHLSPlaybackMode !== HLSPlaybackMode.LIVE && _jsx(Stack, { direction: 'row', children: TimePeriodSelector }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: `Player State: ${playerState}` })] }), _jsx(Stack, { direction: 'row', spacing: 2, alignItems: 'flex-start', width: '100%', children: _jsx(AWS_KVS_Player, { playerEventFns, PlayerComponent: HLS_Player }) })] }));
};
const VideoMonitorTabInternal = memo(VideoMonitorTabInternalComponent);
